// src/pages/AddItemInnerSubCategory.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import InventoryNav from "../components/inventoryNav";
import Time from "../components/time";
import Swal from "sweetalert2";
import { Link, useParams } from "react-router-dom";
import EditItemCategoryModal from "../components/addItemEditModal";
import { hasPermission } from "../utils/permissionHelper";
import BulkInnerSubCategoryUpload from "../components/bulkInnerSubCategoryUpload";

const API = process.env.REACT_APP_API_BASE || "https://rfs2.infraconsoft.net.in/api";
const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 350;

export default function AddItemInnerSubCategory() {
  useAuthRedirect();

  const { catCode, itemName, subCode, subItem } = useParams(); // A001, Floor Mop, SCA001, Sub name
  const branch = localStorage.getItem("branch");
  const userId = localStorage.getItem("username");

  const [formData, setFormData] = useState({ catName: "", subCatCode: "" });

  // Server-paginated list
  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(1);
  const pageRef             = useRef(1);

  const [q, setQ]           = useState("");
  const qRef                = useRef("");
  const [loading, setLoading] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);

  const canView  = hasPermission("Item Code Generation", "view");
  const canEntry = hasPermission("Item Code Generation", "entry");
  const canEdit  = hasPermission("Item Code Generation", "edit");
  const canDel   = hasPermission("Item Code Generation", "del");

  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { qRef.current    = q;    }, [q]);

  // Fetch new ISC code
  const fetchNextCode = useCallback(async (signal) => {
    if (!catCode) return;
    try {
      Swal.fire({
        title: "Loading Inner Sub Code",
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      // Uses MAIN to compute ISC prefix; linkcode stored later as subCode
      const { data } = await axios.get(`${API}/itemInnerSubCat_no`, {
        params: { mainCode: catCode },
        signal,
        validateStatus: (s) => s >= 200 && s < 500,
      });

      const code = data?.newCode;
      if (!code) throw new Error(data?.error || "No code returned");
      setFormData((p) => ({ ...p, subCatCode: code }));
      Swal.close();
    } catch (e) {
      Swal.close();
      if (axios.isCancel?.(e)) return;
      Swal.fire({ icon: "error", title: "Error", text: e.response?.data?.error || e.message });
    }
  }, [catCode]);

  // Server-side page fetch
  const fetchPage = useCallback(async (p, showLoader = true) => {
    if (!subCode) return;  // parent is SubCategory
    try {
      if (showLoader) setLoading(true);
      const { data } = await axios.get(`${API}/itemInnerSubCat_list`, {
        params: { catCode: subCode, page: p, pageSize: PAGE_SIZE, q: qRef.current }
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error("itemInnerSubCat_list error:", e);
      Swal.fire({ icon: "error", title: "Error", text: "Failed to load list." });
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [subCode]);

  // Page change helper (clamped)
  const goTo = useCallback(async (p) => {
    const clamped = Math.max(1, Math.min(p, Math.max(1, Math.ceil(total / PAGE_SIZE))));
    setPage(clamped);
    await fetchPage(clamped);
  }, [fetchPage, total]);

  // Initial load & on parent change
  useEffect(() => {
    if (!subCode || !catCode) return;
    const controller = new AbortController();
    (async () => {
      setPage(1);
      await fetchNextCode(controller.signal); // ISC...
      await fetchPage(1);
    })();
    return () => { controller.abort(); try { Swal.close(); } catch {} };
  }, [subCode, catCode, fetchNextCode, fetchPage]);

  // Debounced search -> server
  useEffect(() => {
    const id = setTimeout(() => { setPage(1); fetchPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [q, fetchPage]);

  // Helper after create/delete to refresh and get a new code (no hard reload needed)
  const refreshAfterCommit = useCallback(async () => {
    await fetchPage(pageRef.current);
    await fetchNextCode();
  }, [fetchPage, fetchNextCode]);

  // Submit (manual add)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.catName.trim()) {
      Swal.fire({ icon: "warning", title: "Empty Field", text: "Please enter a valid Inner Sub category name!" });
      return;
    }

    const payload = {
      branch,
      user: userId,
      mainCode: subCode,               // linkcode = sub category (SCA001)
      subCode: formData.subCatCode,    // ISC...
      name: formData.catName.trim(),
    };

    try {
      Swal.fire({ title: "Submitting...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const { data } = await axios.post(`${API}/itemInnerSubCat_insert`, payload);
      if (data.success) {
        Swal.fire({ icon: "success", title: "Success", text: "Item Inner Sub Category added!" });
        setFormData((p) => ({ ...p, catName: "" }));
        await refreshAfterCommit();      // **auto-refresh without page reload**
      } else {
        Swal.fire({ icon: "error", title: "Error", text: data.message || "Submission failed." });
      }
    } catch (e) {
      Swal.fire({ icon: "error", title: "Error", text: e.response?.data?.message || e.message });
    }
  };

  // Delete
  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete the inner sub category.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        Swal.fire({ title: "Deleting...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        await axios.delete(`${API}/itemInnerSubCat_delete/${id}`);
        Swal.fire({ icon: "success", title: "Deleted!", text: "Inner Sub Category deleted." });
        await refreshAfterCommit();      // refresh current page and code
      } catch (err) {
        console.error("Delete error:", err);
        Swal.fire({ icon: "error", title: "Error!", text: err.response?.data?.message || "Failed to delete inner sub category." });
      }
    });
  };

  // Pagination widgets
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const indexOfFirstItem = (page - 1) * PAGE_SIZE;

  const pages = useMemo(() => {
    const out = [];
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      for (let p = 1; p <= totalPages; p++) out.push(p);
      return out;
    }
    out.push(1);
    const start = Math.max(2, page - 1);
    const end   = Math.min(totalPages - 1, page + 1);
    if (start > 2) out.push("…");
    for (let p = start; p <= end; p++) out.push(p);
    if (end < totalPages - 1) out.push("…");
    out.push(totalPages);
    return out;
  }, [page, totalPages]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({ id: "", catName: "" });
  const openEditModal  = (item) => { setEditFormData({ id: item.id, catNo: item.code, catName: item.itemName }); setIsEditModalOpen(true); };
  const closeEditModal = () => setIsEditModalOpen(false);

  if (!canView) {
    return (
      <div id="admin-container">
        <InventoryNav />
        <div className="content-right" style={{ padding: 20 }}>No access</div>
      </div>
    );
  }

  return (
    <div id="admin-container">
      <InventoryNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Item Inner Sub Category</h2>
            <Link to={`/inventory/add_Item_Subcategory/${catCode}/${itemName}`}>
              <p className="back-button"><i className="fa-solid fa-backward"></i>Back</p>
            </Link>
          </div>
          <Time />
        </section>

        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          {canEntry && (
            <button className="submit-btn" type="button" onClick={() => setBulkOpen(true)}>
              CSV Upload
            </button>
          )}
        </div>

        <div className="form-container">
          {canEntry && (
            <form className="vehicle-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Category Code :</label>
                  <input type="text" value={catCode} readOnly style={{background:'#f8c9bf', fontWeight:'bold'}} />
                </div>
                <div className="form-group">
                  <label>Category Name :</label>
                  <input type="text" value={itemName} readOnly style={{background:'#f8c9bf', fontWeight:'bold'}} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Sub Category Code :</label>
                  <input type="text" value={subCode} readOnly style={{background:'#f8c9bf', fontWeight:'bold'}} />
                </div>
                <div className="form-group">
                  <label>Sub Category Name :</label>
                  <input type="text" value={subItem} readOnly style={{background:'#f8c9bf', fontWeight:'bold'}} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Inner Sub Category<br/>Code :</label>
                  <input type="text" name="subCatCode" value={formData.subCatCode} readOnly />
                </div>
                <div className="form-group">
                  <label>Inner Sub Category <br/>Name :</label>
                  <input
                    type="text"
                    name="catName"
                    value={formData.catName}
                    onChange={(e)=>setFormData(p=>({...p,catName:e.target.value}))}
                    placeholder="Enter Inner Sub Cat Name"
                  />
                </div>
              </div>

              <div className="btn-cls">
                <button type="submit" className="submit-btn">Save</button>
              </div>
            </form>
          )}

          {/* Search (server-side) */}
          <div className="search-filter">
            <p>Search: </p>
            <input
              type="text"
              placeholder="Search by Category Listing"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="search-input"
            />
            <span style={{ marginLeft: 10, opacity: .7 }}>Total: {total}</span>
          </div>

          {/* Table */}
          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Category Listing</th>
                  {canEdit && <th>Edit</th>}
                  <th>Deep Inner Sub Category</th>
                  {canDel && <th>Delete</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="loading-text">Loading data, please wait...</td></tr>
                ) : items.length > 0 ? (
                  items.map((item, index) => (
                    <tr key={item.id ?? `${item.code}-${index}`}>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>{item.code} - ({item.itemName})</td>
                      {canEdit && (
                        <td>
                          <button style={{ background:'transparent', border:'none', color:'blue', cursor:'pointer' }}
                                  title="Edit" onClick={() => openEditModal(item)}>
                            <i className="fa-solid fa-pen-to-square"></i>
                          </button>
                        </td>
                      )}
                      <td>
                        <Link to={`/inventory/add_deep_Inner_Subcategory/${catCode}/${itemName}/${subCode}/${subItem}/${item.code}/${item.itemName}`}>
                          Click
                        </Link>
                      </td>
                      {canDel && (
                        <td className="del">
                          {item.childCount > 0 ? "No Access"
                            : <i className="fa-solid fa-trash-can" onClick={() => handleDelete(item.id)}></i>}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="6">No Data Available</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <button onClick={() => goTo(page - 1)} disabled={page === 1}>Previous</button>
            {pages.map((p, i) =>
              p === "…" ? (
                <span key={`dots-${i}`} style={{ padding: "0 .5rem" }}>…</span>
              ) : (
                <button key={`p-${p}`} onClick={() => goTo(p)} className={page === p ? "active" : ""}>
                  {p}
                </button>
              )
            )}
            <button onClick={() => goTo(page + 1)} disabled={page === totalPages}>Next</button>
          </div>
        </div>
      </div>

      <EditItemCategoryModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        fetchData={() => fetchPage(pageRef.current)}
      />

      {canEntry && (
        <BulkInnerSubCategoryUpload
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onCommitted={refreshAfterCommit}   
          mainCode={catCode}                   // A001 (drives ISC prefix)
          subCode={subCode}                    // SCA001 (linkcode)
        />
      )}
    </div>
  );
}
